import {useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {
  IDENTITY_VALIDATORS,
  PersonaBasicFields,
  PersonaContactFields,
  RosterCreateSuccess,
  clearChangedIdentityErrors,
  identityPayload,
  type PersonaIdentity,
} from '@/components/roster'
import {QuotaMatrixInput} from '@/components/partner-quota-matrix'
import {StepProgress} from '@/components/step-progress'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {FieldError} from '@/components/field-error'
import {Hint} from '@/components/ui/hint'
import {HelpIcon} from '@/components/help-icon'
import {useCreatePartner, type PartnerTipo} from '@/hooks/inci/aliados/use-partners'
import {translateApiError} from '@/lib/errors'
import {validateCedula, validateEmail, validateNit, validatePhone, validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

const EMPTY_IDENTITY: PersonaIdentity = {
  name: '',
  last_name: '',
  cedula: '',
  tipo_documento: 'CC',
  email: '',
  phone: '',
}
const TOTAL_STEPS = 3
const STEP_KEYS = ['datos', 'cuenta', 'cupos'] as const

function CreatedCode({code}: {code: string}) {
  const {t} = useTranslation()
  return (
    <div className="surface rounded-lg px-4 py-3">
      <p className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
        {t('partners.create.codeLabel')}
      </p>
      <p className="mt-1 font-mono text-lg tracking-wider text-foreground" data-testid="partner-created-code">
        {code}
      </p>
      <Hint className="mt-1">{t('partners.create.codeHint')}</Hint>
    </div>
  )
}

type Result = {inviteToken: string | null; code: string}

/** NIT and razón social are required exactly when jurídica (`PartnerCreate._check_juridica_fields`). */
function TipoFields({
  tipo,
  onTipoChange,
  nit,
  onNitChange,
  onNitBlur,
  nitError,
  razonSocial,
  onRazonSocialChange,
  onRazonSocialBlur,
  razonSocialError,
}: {
  tipo: PartnerTipo
  onTipoChange: (tipo: PartnerTipo) => void
  nit: string
  onNitChange: (nit: string) => void
  onNitBlur: () => void
  nitError?: string
  razonSocial: string
  onRazonSocialChange: (razonSocial: string) => void
  onRazonSocialBlur: () => void
  razonSocialError?: string
}) {
  const {t} = useTranslation()
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="partner-tipo">{t('partners.create.tipo')}</Label>
        <select
          id="partner-tipo"
          data-testid="partner-tipo"
          value={tipo}
          onChange={(e) => onTipoChange(e.target.value as PartnerTipo)}
          className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm">
          <option value="natural">{t('partners.tipo.natural')}</option>
          <option value="juridica">{t('partners.tipo.juridica')}</option>
        </select>
      </div>
      {tipo === 'juridica' && (
        <>
          <div className="space-y-2">
            <Label htmlFor="partner-nit">{t('partners.create.nit')}</Label>
            <Input
              id="partner-nit"
              data-testid="partner-nit"
              value={nit}
              onChange={(e) => onNitChange(e.target.value)}
              onBlur={onNitBlur}
              maxLength={tope('NIT')}
              placeholder={t('partners.create.nitPlaceholder')}
              aria-invalid={!!nitError}
              required
            />
            <FieldError error={nitError} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="partner-razon-social">{t('partners.create.razonSocial')}</Label>
            <Input
              id="partner-razon-social"
              data-testid="partner-razon-social"
              value={razonSocial}
              onChange={(e) => onRazonSocialChange(e.target.value)}
              onBlur={onRazonSocialBlur}
              maxLength={120}
              aria-invalid={!!razonSocialError}
              required
            />
            <FieldError error={razonSocialError} />
          </div>
        </>
      )}
    </>
  )
}

export function PartnerCreateDialog({open, onClose}: {open: boolean; onClose: () => void}) {
  const {t} = useTranslation()
  const create = useCreatePartner()
  const [step, setStep] = useState(1)
  const [identity, setIdentity] = useState<PersonaIdentity>(EMPTY_IDENTITY)
  const [errors, setErrors] = useState<Partial<Record<keyof PersonaIdentity, string>>>({})
  const [tipo, setTipo] = useState<PartnerTipo>('natural')
  const [nit, setNit] = useState('')
  const [razonSocial, setRazonSocial] = useState('')
  const [nitError, setNitError] = useState<string | undefined>(undefined)
  const [razonSocialError, setRazonSocialError] = useState<string | undefined>(undefined)
  const [quotas, setQuotas] = useState<Record<string, number>>({})
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<Result | null>(null)
  // El popover de ayuda se porta al `DialogContent`, no al body: ver `ui/popover.tsx`.
  const [dialogo, setDialogo] = useState<HTMLElement | null>(null)

  function reset() {
    setStep(1)
    setIdentity(EMPTY_IDENTITY)
    setErrors({})
    setTipo('natural')
    setNit('')
    setRazonSocial('')
    setNitError(undefined)
    setRazonSocialError(undefined)
    setQuotas({})
    setError(null)
    setResult(null)
    create.reset()
  }

  function handleOpenChange(next: boolean) {
    if (!next) {
      reset()
      onClose()
    }
  }

  function onIdentityChange(next: PersonaIdentity) {
    setIdentity(next)
    setErrors((prev) => clearChangedIdentityErrors(prev, next, identity))
  }

  function blurIdentityField(field: keyof PersonaIdentity) {
    setErrors((prev) => ({...prev, [field]: IDENTITY_VALIDATORS[field](identity[field], identity) ?? undefined}))
  }

  const step1Valid =
    Boolean(identity.name && identity.last_name && identity.cedula) &&
    validateRequired(identity.name) === null &&
    validateRequired(identity.last_name) === null &&
    validateCedula(identity.cedula, identity.tipo_documento) === null &&
    (tipo === 'natural' ||
      (Boolean(nit && razonSocial) && validateNit(nit) === null && validateRequired(razonSocial) === null))

  const step2Valid =
    Boolean(identity.email) &&
    validateEmail(identity.email) === null &&
    (!identity.phone.trim() || validatePhone(identity.phone) === null)

  function goNext() {
    setError(null)
    if (step === 1 && !step1Valid) return
    if (step === 2 && !step2Valid) return
    setStep((s) => Math.min(s + 1, TOTAL_STEPS))
  }

  function goBack() {
    setError(null)
    setStep((s) => Math.max(s - 1, 1))
  }

  function submit() {
    setError(null)
    create.mutate(
      {
        ...identityPayload(identity),
        quotas,
        tipo,
        nit: tipo === 'juridica' ? nit.trim() : null,
        razon_social: tipo === 'juridica' ? razonSocial.trim() : null,
      },
      {
        onSuccess: (data) => setResult({inviteToken: data.invite_token ?? null, code: data.partner.code}),
        onError: (err) => setError(translateApiError(err)),
      }
    )
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (step < TOTAL_STEPS) {
      goNext()
      return
    }
    submit()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent ref={setDialogo} data-testid="partner-create-dialog">
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>{t('partners.create.title')}</DialogTitle>
            <HelpIcon label={t('partners.create.tipLabel')} testId="partner-create-tip" contenedor={dialogo}>
              {t('partners.create.description')}
            </HelpIcon>
          </div>
        </DialogHeader>
        {result !== null ? (
          <RosterCreateSuccess
            description={t('partners.create.description')}
            extra={<CreatedCode code={result.code} />}
            inviteToken={result.inviteToken}
            onClose={() => handleOpenChange(false)}
          />
        ) : (
          <>
            <StepProgress
              step={step}
              total={TOTAL_STEPS}
              testId="partner-create-progress"
              label={`${t('partners.create.stepIndicator', {step, total: TOTAL_STEPS})} · ${t(`partners.create.steps.${STEP_KEYS[step - 1]}`)}`}
            />
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              {step === 1 && (
                <fieldset className="space-y-4" data-testid="partner-step-datos">
                  <legend className="sr-only">{t('partners.create.steps.datos')}</legend>
                  <TipoFields
                    tipo={tipo}
                    onTipoChange={setTipo}
                    nit={nit}
                    onNitChange={(v) => {
                      setNit(v)
                      setNitError(undefined)
                    }}
                    onNitBlur={() => setNitError(validateNit(nit) ?? undefined)}
                    nitError={nitError}
                    razonSocial={razonSocial}
                    onRazonSocialChange={(v) => {
                      setRazonSocial(v)
                      setRazonSocialError(undefined)
                    }}
                    onRazonSocialBlur={() => setRazonSocialError(validateRequired(razonSocial) ?? undefined)}
                    razonSocialError={razonSocialError}
                  />
                  <PersonaBasicFields
                    value={identity}
                    onChange={onIdentityChange}
                    tKey="partners.create"
                    testPrefix="partner"
                    errors={errors}
                    onBlurField={blurIdentityField}
                  />
                </fieldset>
              )}

              {step === 2 && (
                <fieldset className="space-y-4" data-testid="partner-step-cuenta">
                  <legend className="sr-only">{t('partners.create.steps.cuenta')}</legend>
                  <PersonaContactFields
                    value={identity}
                    onChange={onIdentityChange}
                    tKey="partners.create"
                    testPrefix="partner"
                    errors={errors}
                    onBlurField={blurIdentityField}
                  />
                </fieldset>
              )}

              {step === 3 && (
                <fieldset data-testid="partner-step-cupos">
                  <legend className="sr-only">{t('partners.create.steps.cupos')}</legend>
                  <QuotaMatrixInput value={quotas} onChange={setQuotas} idPrefix="partner-quota" />
                </fieldset>
              )}

              {error && <p className="text-sm text-destructive">{error}</p>}

              <div className="flex justify-between gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  data-testid={step === 1 ? 'partner-create-cancel' : 'partner-create-back'}
                  onClick={step === 1 ? () => handleOpenChange(false) : goBack}>
                  {step === 1 ? t('common.cancel') : t('common.back')}
                </Button>
                {/* `key` por rama: sin él React reusa el nodo y el click de «Siguiente» cambia su
                    `type` a submit mientras sigue vivo, así que el navegador envía el formulario. */}
                {step < TOTAL_STEPS ? (
                  <Button
                    key="next"
                    type="button"
                    data-testid="partner-step-next"
                    disabled={step === 1 ? !step1Valid : !step2Valid}
                    onClick={goNext}>
                    {t('partners.create.next')}
                  </Button>
                ) : (
                  <Button key="submit" type="submit" data-testid="partner-create-submit" disabled={create.isPending}>
                    {create.isPending ? t('partners.create.creating') : t('partners.create.submit')}
                  </Button>
                )}
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
